import React, { useState, useEffect } from 'react';
import { LIMITES, avisaDelTope } from '../../limites';
import './PreferencesSection.css'; // Usamos el nuevo archivo de estilos

// ── LO QUE VIENE DE LA BASE NO SIEMPRE ES LO QUE ESPERAMOS (19-09-2026) ──────
// Rob: «en el dashboard, Perfil me manda pantalla en blanco». Era esto: si el
// perfil de aprendizaje tiene `purposes` en NULL —y lo está en las cuentas que
// no terminaron el onboarding—, `purposes.includes(...)` reventaba al pintar
// las casillas. Sin nada que atrape el error, React desmonta el árbol entero:
// no se ve un fallo, se ve una pantalla vacía.
//
// La forma de no volver a pisar esto no es recordar el `|| []` en cada sitio,
// sino que el perfil entre a la pantalla ya con la forma correcta.
const PERFIL_VACIO = {
    profession: '', learning_focus: 'mixed', speaking_confidence: 'medium',
    purposes: [], other_purpose: '', example_context: 'mixed',
};

function normaliza(perfil) {
    const p = perfil || {};
    return {
        ...PERFIL_VACIO,
        ...p,
        // Un campo de texto con `null` deja de ser controlado y React avisa;
        // una lista con `null` tumba la pantalla.
        // Se recorta al entrar: alguna cuenta vieja guardó una profesión más
        // larga de lo que el servidor acepta hoy, y sin esto no podría volver a
        // guardar sus preferencias nunca (un 422 en cada intento).
        profession: (p.profession ?? '').slice(0, LIMITES.profesion),
        other_purpose: p.other_purpose ?? '',
        learning_focus: p.learning_focus || PERFIL_VACIO.learning_focus,
        example_context: p.example_context || PERFIL_VACIO.example_context,
        speaking_confidence: p.speaking_confidence || PERFIL_VACIO.speaking_confidence,
        purposes: Array.isArray(p.purposes) ? p.purposes : [],
    };
}

function PreferencesSection() {
    const [learningProfile, setLearningProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch("/api/profile/me", {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(res => res.json())
        .then(data => {
            setLearningProfile(normaliza(data.learning_profile));
            setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLearningProfile(prev => ({ ...prev, [name]: value }));
    };

    const handlePurposesChange = (e) => {
        const { value, checked } = e.target;
        setLearningProfile(prev => {
            const actuales = Array.isArray(prev.purposes) ? prev.purposes : [];
            const newPurposes = checked
                ? [...actuales, value]
                : actuales.filter(p => p !== value);
            return { ...prev, purposes: newPurposes };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        
        fetch("/api/profile/me/learning-profile", {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            // Normalizado otra vez: lo que sale hacia la base tampoco puede
            // llevar un `null` donde se espera una lista.
            body: JSON.stringify(normaliza(learningProfile))
        })
        .then(res => {
            if (!res.ok) { throw new Error("Error del servidor"); }
            return res.json();
        })
        .then(data => {
            setLearningProfile(normaliza(data));
            alert("¡Preferencias de aprendizaje actualizadas con éxito!");
        })
        .catch(() => alert("Ocurrió un error al actualizar tus preferencias."));
    };

    if (isLoading) {
        return <div className="dp-progress-card dp-neumorphic"><p>Cargando preferencias...</p></div>;
    }

    if (!learningProfile) {
        return <div className="dp-progress-card dp-neumorphic"><p>No se pudo cargar tu perfil de aprendizaje.</p></div>;
    }
    
    return (
        <div className="dp-progress-card dp-neumorphic">
            <h2 className="dp-section-title">⚙️ Mis Preferencias de Aprendizaje</h2>
            <p>Ajusta cómo quieres que el Tutor IA personalice tus lecciones.</p>

            <form onSubmit={handleSubmit} className="ps-form">
                <div className="ps-form-group">
                    <label className="ps-form-label">
                        Mi Profesión
                        {/* El número sale del mismo sitio que el del servidor.
                            Escrito a mano decía 120 donde el esquema acepta 80:
                            la caja dejaba escribir el doble de lo que se puede
                            guardar, y el alumno se comía un 422 mudo. */}
                        <span className={`ps-contador ${avisaDelTope(learningProfile.profession.length, LIMITES.profesion) ? 'cerca' : ''}`}>
                            {learningProfile.profession.length}/{LIMITES.profesion}
                        </span>
                    </label>
                    <input name="profession" type="text" className="ps-input"
                        maxLength={LIMITES.profesion}
                        placeholder="Ej: enfermera, mesero, estudiante de prepa…"
                        value={learningProfile.profession} onChange={handleInputChange} />
                </div>

                <div className="ps-form-group">
                    <label className="ps-form-label">Enfoque del Vocabulario</label>
                    <select name="learning_focus" className="ps-input" value={learningProfile.learning_focus} onChange={handleInputChange}>
                        <option value="professional">Enfocado en mi profesión</option>
                        <option value="mixed">50/50 (profesional y general)</option>
                        <option value="general">Vocabulario general</option>
                    </select>
                </div>

                <div className="ps-form-group">
                    <label className="ps-form-label">Contexto de los Ejemplos</label>
                    <select name="example_context" className="ps-input" value={learningProfile.example_context} onChange={handleInputChange}>
                        <option value="professional">Profesional</option>
                        <option value="educational">Educativo</option>
                        <option value="everyday">Cotidiano</option>
                        <option value="youthful">Infantil o juvenil</option>
                        <option value="mixed">Mixto</option>
                    </select>
                </div>

                <div className="ps-form-group">
                    <label className="ps-form-label">Mis Propósitos</label>
                    <div className="ps-checkbox-group">
                        {['Mejorar mi carrera', 'Viajar cómodamente', 'Hablar con fluidez', 'Entender cultura', 'Escribir mejor', 'Uso académico'].map(purpose => (
                            <label key={purpose} className="ps-checkbox-label">
                                <input 
                                    type="checkbox" 
                                    value={purpose}
                                    checked={(learningProfile.purposes || []).includes(purpose)}
                                    onChange={handlePurposesChange}
                                />
                                {purpose}
                            </label>
                        ))}
                    </div>
                </div>
                
                <button type="submit" className="ps-btn">Guardar Preferencias</button>
            </form>
        </div>
    );
}

export default PreferencesSection;